/**
 * F-076 — Texas Content Generation AI content studio (E-022 FE).
 * Batch-8 item originally assigned to antigravity (assign
 * m-2026-10-15T140000Z-assign-batch8-antigravity); taken over by the wsl lane
 * after antigravity stopped at branch creation with no work committed.
 *
 * The E-022 content-generation BE (buffy) was retracted with the batch-8
 * dispatch and never published, so generate/publish bind to the nominated
 * paths CONTENT_STUDIO_PATHS and degrade to null. Nothing in this module
 * fabricates generated prose: with the engine down the studio still offers the
 * prompt builder and deterministic SEO metadata, and says plainly that the
 * content engine is not live yet.
 *
 * Existing Claude capacity lives in LP src/routes/analysis.js (POST
 * /api/analysis/llm, trial-guarded) — noted for the BE lane, not called here,
 * because a document-analysis contract is not a content-generation contract.
 */
import { landlordApi } from './api';

export type ContentChannel = 'blog' | 'email' | 'social';

export interface ContentTopic {
    id: string;
    title: string;
    channel: ContentChannel;
    brief: string;
    keywords: string[];
    reference: string;
}

export const TX_CONTENT_TOPICS: ContentTopic[] = [
    {
        id: 'sb38-guide',
        title: 'SB 38 compliance guide',
        channel: 'blog',
        brief: 'A landlord-facing guide to SB 38 notice timing, non-delegable obligations and the evidence that proves each notice was served correctly.',
        keywords: ['Texas SB 38', 'eviction notice', 'notice window', 'landlord compliance'],
        reference: 'Tex. Prop. Code ch. 24 (SB 38)',
    },
    {
        id: 'houston-asec',
        title: 'Houston Ch. 10 ASEC walkthrough',
        channel: 'blog',
        brief: 'How Houston property owners complete the Annual Self-Inspection Certification under Ch. 10 Art. II, close deficiencies and retain proof.',
        keywords: ['Houston Ch. 10', 'ASEC', 'self-inspection certification', 'Houston rental'],
        reference: 'City of Houston Code of Ordinances Ch. 10 Art. II',
    },
    {
        id: 'dallas-ch27',
        title: 'Dallas Ch. 27 registration walkthrough',
        channel: 'blog',
        brief: 'Registering a rental property under Dallas Ch. 27, tracking the annual renewal and keeping portfolio registration posture current.',
        keywords: ['Dallas Ch. 27', 'rental registration', 'Dallas renewal', 'property registration'],
        reference: 'Dallas City Code Ch. 27',
    },
    {
        id: 'tx-landlord-faq',
        title: 'Texas landlord FAQ',
        channel: 'email',
        brief: 'Plain-answers FAQ covering deposits, notice periods, subchapter D obligations and municipal registration for Texas rental owners.',
        keywords: ['Texas landlord FAQ', 'deposits', 'subchapter D', 'Texas rentals'],
        reference: 'Tex. Prop. Code ch. 92',
    },
];

export interface SeoMeta {
    title: string;
    slug: string;
    description: string;
    keywords: string[];
}

export const CONTENT_STUDIO_PATHS = {
    generate: 'api/content/generate',
    publish: 'api/content/publish',
} as const;

export function slugify(input: string): string {
    return String(input)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);
}

export function buildSeoMeta(topic: ContentTopic, extraKeywords: string[] = []): SeoMeta {
    const keywords = [...topic.keywords, ...extraKeywords].filter(Boolean).slice(0, 8);
    return {
        title: topic.title.slice(0, 60),
        slug: slugify(topic.title),
        description: topic.brief.slice(0, 155),
        keywords,
    };
}

export function buildPrompt(topic: ContentTopic, opts: { audience?: string; words?: number } = {}): string {
    const audience = opts.audience || 'Texas rental property owners';
    const words = opts.words || 900;
    return [
        `Write a ${words}-word ${topic.channel} for ${audience}.`,
        `Topic: ${topic.title}.`,
        `Cover: ${topic.brief}`,
        `Authoritative reference: ${topic.reference}.`,
        `Target keywords: ${topic.keywords.join(', ')}.`,
        'Optimise the title tag, meta description and slug for search, keep headings scannable, and end with one clear call to action.',
    ].join(' ');
}

export interface GeneratedContent {
    topicId: string;
    channel: ContentChannel;
    seo: SeoMeta;
    title: string;
    body: string;
    wordCount: number;
    generatedBy?: string;
}

const unwrapBody = (res: any): any => {
    const body = res?.data;
    if (body && body.success === false) return null;
    return body && body.data !== undefined ? body.data : body;
};

const toGenerated = (topicId: string, data: any): GeneratedContent | null => {
    if (!data || typeof data.body !== 'string') return null;
    const topic = TX_CONTENT_TOPICS.find((t) => t.id === topicId);
    const seo: SeoMeta = {
        title: data.seo?.title || data.title || topic?.title || '',
        slug: data.seo?.slug || slugify(data.title || topicId),
        description: data.seo?.description || topic?.brief.slice(0, 155) || '',
        keywords: Array.isArray(data.seo?.keywords) ? data.seo.keywords : topic?.keywords || [],
    };
    return {
        topicId,
        channel: data.channel || topic?.channel || 'blog',
        seo,
        title: data.title || seo.title,
        body: data.body,
        wordCount: data.wordCount || data.body.split(/\s+/).filter(Boolean).length,
        generatedBy: data.generatedBy,
    };
};

export const contentStudioApi = {
    /** Nominated E-022 contract — null while buffy's content-gen BE is unpublished. */
    generate: (payload: { topicId: string; prompt: string; audience?: string; words?: number }): Promise<GeneratedContent | null> =>
        landlordApi
            .post(CONTENT_STUDIO_PATHS.generate, payload)
            .then((res: any) => toGenerated(payload.topicId, unwrapBody(res)))
            .catch(() => null),

    publish: (payload: { topicId: string; seo: SeoMeta; body: string }): Promise<{ id?: string; status?: string } | null> =>
        landlordApi
            .post(CONTENT_STUDIO_PATHS.publish, payload)
            .then((res: any) => unwrapBody(res))
            .then((data: any) => (data && typeof data === 'object' ? data : null))
            .catch(() => null),
};

export default contentStudioApi;
